import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
    Card, CardContent, TextField, Autocomplete,
    Button, Box, Divider, Typography, Alert,
    Link as MuiLink,
} from "@mui/material";
import { Link } from "react-router-dom";
import api from "../../shared/api";

const schema = z.object({
    email: z.string().min(1, "The email field is required.").email("The email field must be a valid email."),
    arcID: z.number({ required_error: "The program year field is required." }),
});

export default function ForgotUsernameForm({ viewModel }) {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState("");

    const { control, handleSubmit, setValue, setError, formState: { errors } } = useForm({
        resolver: zodResolver(schema),
        defaultValues: { email: "", arcID: null },
    });

    useEffect(() => {
        if (viewModel.programYears.length > 0) {
            setValue("arcID", viewModel.programYears[0].arcID);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = async (data) => {
        setFormError("");
        setLoading(true);
        try {
            const response = await api.post("/forgot-username", data, { responseType: "json" });
            if (response.data.location) {
                window.location.replace(response.data.location);
                return;
            }
            if (!response.data.valid && response.data.errors) {
                response.data.errors.forEach((err) => {
                    if (err.PROPERTY.length === 0) {
                        setFormError((prev) => prev + " " + err.MESSAGE);
                    } else {
                        setError(err.PROPERTY, { message: err.MESSAGE });
                    }
                });
            } else {
                setStep(2);
            }
        } catch {
            setFormError("There was a network error processing this request. Please contact support if this problem persists.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card id="forgotUsername" variant="elevation" elevation={0} square sx={{ border: "none" }}>
            <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h5">Forgot Username</Typography>
            </CardContent>

            {step === 1 && (
                <form onSubmit={handleSubmit(onSubmit)}>
                    <CardContent>
                        <Box sx={{ mx: "auto", maxWidth: "83%" }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Please enter the primary email of your account.
                            </Typography>

                            <Controller
                                name="email"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Email"
                                        fullWidth
                                        error={!!errors.email}
                                        helperText={errors.email?.message}
                                        sx={{ mb: 3 }}
                                        onKeyDown={(e) => e.key === "Enter" && handleSubmit(onSubmit)()}
                                    />
                                )}
                            />

                            {viewModel.programYears.length > 1 && (
                                <Controller
                                    name="arcID"
                                    control={control}
                                    render={({ field }) => (
                                        <Autocomplete
                                            options={viewModel.programYears}
                                            getOptionLabel={(opt) => String(opt.arcYear || "")}
                                            value={viewModel.programYears.find((y) => y.arcID === field.value) || null}
                                            onChange={(_, newVal) => field.onChange(newVal ? newVal.arcID : null)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Program Year"
                                                    error={!!errors.arcID}
                                                    helperText={errors.arcID?.message}
                                                    sx={{ mb: 3 }}
                                                />
                                            )}
                                            disableClearable
                                        />
                                    )}
                                />
                            )}
                        </Box>

                        <Box sx={{ mx: "auto", maxWidth: "83%", pt: 2 }}>
                            {formError ? (
                                <Typography variant="subtitle1" color="error">{formError}</Typography>
                            ) : (
                                <Typography variant="subtitle1">&nbsp;</Typography>
                            )}
                        </Box>

                        <Box sx={{ mx: "auto", maxWidth: "83%", pt: 2 }}>
                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={loading}
                                sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, py: 1.2 }}
                            >
                                {loading ? "Submitting..." : "Submit"}
                            </Button>
                        </Box>
                    </CardContent>
                </form>
            )}

            {step === 2 && (
                <CardContent>
                    <Alert severity="info" variant="outlined">
                        An email containing your username was sent to the email address provided.{" "}
                        <strong><MuiLink component={Link} to="/login">Back to login</MuiLink></strong>
                    </Alert>
                </CardContent>
            )}

            <Divider />
            <CardContent>
                <Box sx={{ mx: "auto", maxWidth: "83%" }}>
                    <MuiLink component={Link} to="/login/forgot-password" display="block">Forgot my password</MuiLink>
                    <MuiLink component={Link} to="/login" display="block">Back to login</MuiLink>
                </Box>
            </CardContent>
        </Card>
    );
}
